/**
 * Taker-side attribution capture.
 *
 * Responsibilities:
 *   1. On page load, if URL has `?att=<blob>` (widget handoff), decode + write cookies.
 *   2. Parse UTM / click-ID / promo params from current URL into first/last-touch cookies.
 *   3. Expose `getAttributionPayload()` returning the merged payload for POST bodies.
 *   4. Expose `getOrCreateSessionId()` for session continuity across taker pages.
 *   5. Mirror the attribution cookies and the session id into localStorage, and
 *      restore a cookie from its mirror when the cookie has gone missing.
 *
 * Mirrors the widget-side module (embed/src/widget.ts) so cookie names + shape match.
 * The taker is on discover.thesuccessfulbookkeeper.com, cookies are scoped to the
 * parent domain when possible so hubspot → discover handoff via cookies also works.
 */

import { stripPersonalParams } from './lib/callPrivacy.ts';

const ATTRIB_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id',
  'gclid', 'fbclid', 'msclkid', 'ttclid', 'li_fat_id',
  'promo_code', 'ref', 'referral_code',
] as const;

export interface CapturedAttribution {
  utm_source?: string; utm_medium?: string; utm_campaign?: string;
  utm_term?: string; utm_content?: string; utm_id?: string;
  gclid?: string; fbclid?: string; msclkid?: string; ttclid?: string; li_fat_id?: string;
  promo_code?: string; referral_code?: string;
  landingPageUri?: string; landingPageTitle?: string; referrer?: string; visitAt?: string;
}

export interface AttributionPayload {
  first?: CapturedAttribution;
  last?: CapturedAttribution;
  submissionPageUri?: string;
  submissionPageTitle?: string;
  sessionId?: string;
  consent?: 'default' | 'opted-out';
  fbp?: string;      // _fbp cookie — Meta-generated browser identifier for CAPI EMQ
  visitorId?: string; // engage_vid (set by GTM on brand pages) or engage_session (taker)
  brandId?: string;   // which brand site generated this submission — for multi-brand attribution
}

const FIRST_COOKIE = 'engage_attrib_first';
const LAST_COOKIE = 'engage_attrib_last';
const SESSION_COOKIE = 'engage_session';
// engage_vid: set by GTM on brand pages (blog, homepage) scoped to parent root domain.
// When a visitor hits the HubSpot blog first, GTM sets this cookie. When they later
// arrive on the taker, we read it here so the blog pageview is stitched to the opt-in.
const VISITOR_ID_COOKIE = 'engage_vid';

const FIRST_TOUCH_MAX_AGE_SECS = 60 * 60 * 24 * 90;
// How long the session-only cookies (last touch, session id) survive in their
// localStorage mirror without being touched. A visit that comes back inside
// this window is the same visit; one that comes back later starts fresh, as it
// would have with the cookies alone.
export const VISIT_RESTORE_WINDOW_SECS = 30 * 60;

// localStorage mirrors (mct_77b38cde-012). Facebook's iOS in-app browser can
// drop the taker's cookies between loads of the same visit: on 13 Oct 2026 one
// paid visitor on summit-2026-na showed up as three visitor ids in 90 seconds,
// and the id the registration carried had landed with no UTMs. The cookie
// stays the primary store; the mirror only refills a cookie that is missing,
// so a working browser behaves exactly as before. Every storage call is
// wrapped: storage that is blocked or full leaves cookie-only behaviour.
const STORE_PREFIX = 'engage_mirror:';
const MIRRORED = new Set([FIRST_COOKIE, LAST_COOKIE, SESSION_COOKIE]);

interface Mirrored { v: string; exp: number }

function storage(): Storage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}

function optedOut(): boolean {
  return readCookie('engage_consent') === 'opted-out';
}

function writeMirror(name: string, value: string, ttlSecs: number): void {
  const st = storage();
  if (!st || optedOut()) return;
  try {
    const rec: Mirrored = { v: value, exp: Date.now() + ttlSecs * 1000 };
    st.setItem(STORE_PREFIX + name, JSON.stringify(rec));
  } catch { /* blocked or full: cookie-only */ }
}

function readMirror(name: string): string | null {
  const st = storage();
  if (!st || optedOut()) return null;
  try {
    const raw = st.getItem(STORE_PREFIX + name);
    if (!raw) return null;
    const rec = JSON.parse(raw) as Mirrored;
    if (typeof rec?.v !== 'string' || typeof rec.exp !== 'number' || rec.exp <= Date.now()) {
      st.removeItem(STORE_PREFIX + name);
      return null;
    }
    return rec.v;
  } catch { return null; }
}

function clearMirrors(): void {
  const st = storage();
  if (!st) return;
  for (const name of MIRRORED) {
    try { st.removeItem(STORE_PREFIX + name); } catch { /* */ }
  }
}

function mirrorTtl(name: string): number {
  return name === FIRST_COOKIE ? FIRST_TOUCH_MAX_AGE_SECS : VISIT_RESTORE_WINDOW_SECS;
}

/** Cookie first; if it is gone, the unexpired mirror, written back into the
 *  cookie so the rest of the page sees it. Session-window mirrors are
 *  refreshed on every read, so an active visit never times out mid-way. */
function readDurable(name: string): string | null {
  const fromCookie = readCookie(name);
  if (fromCookie) {
    // First touch keeps its original expiry; it is only mirrored when the
    // mirror is missing (a visitor whose cookie predates the mirror).
    if (name !== FIRST_COOKIE || readMirror(name) === null) writeMirror(name, fromCookie, mirrorTtl(name));
    return fromCookie;
  }
  const fromMirror = readMirror(name);
  if (!fromMirror) return null;
  const domain = parentDomain();
  if (name === FIRST_COOKIE) writeCookie(name, fromMirror, { maxAge: FIRST_TOUCH_MAX_AGE_SECS, domain });
  else {
    writeCookie(name, fromMirror, { domain });
    writeMirror(name, fromMirror, mirrorTtl(name));
  }
  return fromMirror;
}

/** Write a mirrored cookie and its mirror together. */
function writeDurable(name: string, value: string): void {
  const domain = parentDomain();
  if (name === FIRST_COOKIE) writeCookie(name, value, { maxAge: FIRST_TOUCH_MAX_AGE_SECS, domain });
  else writeCookie(name, value, { domain });
  writeMirror(name, value, mirrorTtl(name));
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  if (!match) return null;
  try { return decodeURIComponent(match[1]); } catch { return null; }
}

function writeCookie(name: string, value: string, opts: { maxAge?: number; domain?: string } = {}): void {
  const parts = [`${name}=${encodeURIComponent(value)}`, 'Path=/', 'SameSite=Lax'];
  if (location.protocol === 'https:') parts.push('Secure');
  if (opts.maxAge) parts.push(`Max-Age=${opts.maxAge}`);
  if (opts.domain) parts.push(`Domain=${opts.domain}`);
  document.cookie = parts.join('; ');
}

function parentDomain(): string | undefined {
  const host = location.hostname;
  if (!host || /^\d+\.\d+\.\d+\.\d+$/.test(host) || host === 'localhost') return undefined;
  const parts = host.split('.');
  if (parts.length < 2) return undefined;
  return '.' + parts.slice(-2).join('.');
}

function parseAttribFromUrl(): CapturedAttribution {
  const params = new URLSearchParams(location.search);
  const out: CapturedAttribution = {};
  for (const k of ATTRIB_PARAMS) {
    const v = params.get(k);
    if (v && v.trim()) {
      const target = (k === 'ref' ? 'referral_code' : k) as keyof CapturedAttribution;
      out[target] = v.trim().slice(0, 255);
    }
  }
  return out;
}

function hasAny(a: CapturedAttribution): boolean {
  return Object.keys(a).some((k) => !!(a as Record<string, string | undefined>)[k]);
}

/** Decode a widget-handoff `att` blob. Mirrors embed/src/widget.ts encodeAttribForUrl. */
function decodeAttBlob(blob: string): AttributionPayload | null {
  try {
    const pad = blob.length % 4 === 0 ? '' : '='.repeat(4 - (blob.length % 4));
    const b64 = blob.replace(/-/g, '+').replace(/_/g, '/') + pad;
    const json = decodeURIComponent(escape(atob(b64)));
    return JSON.parse(json) as AttributionPayload;
  } catch { return null; }
}

export function getOrCreateSessionId(): string {
  let sid = readDurable(SESSION_COOKIE);
  if (sid) return sid;
  sid = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `ses-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  writeDurable(SESSION_COOKIE, sid);
  return sid;
}

/** Idempotent: call once per page mount. Hydrates cookies from either URL params or the
 *  widget-handoff `att` blob (widget → taker redirect carries this). Prefers existing
 *  cookies — never overwrites a first-touch that's already set. */
export function hydrateAttribution(): void {
  if (readCookie('engage_consent') === 'opted-out') { clearMirrors(); return; }

  const now = new Date().toISOString();

  // 1. Widget handoff path: `att` query param.
  const handoffBlob = new URLSearchParams(location.search).get('att');
  if (handoffBlob) {
    const decoded = decodeAttBlob(handoffBlob);
    if (decoded) {
      if (decoded.first && !readDurable(FIRST_COOKIE)) {
        writeDurable(FIRST_COOKIE, JSON.stringify(decoded.first));
      }
      if (decoded.last) {
        writeDurable(LAST_COOKIE, JSON.stringify(decoded.last));
      }
    }
  }

  // 2. Direct-nav path: parse UTMs from current URL.
  const parsed = parseAttribFromUrl();
  if (hasAny(parsed)) {
    if (!readDurable(FIRST_COOKIE)) {
      const first: CapturedAttribution = {
        ...parsed,
        landingPageUri: stripPersonalParams(location.href).slice(0, 255),
        landingPageTitle: document.title.slice(0, 255),
        referrer: document.referrer ? document.referrer.slice(0, 255) : undefined,
        visitAt: now,
      };
      writeDurable(FIRST_COOKIE, JSON.stringify(first));
    }
    writeDurable(LAST_COOKIE, JSON.stringify({ ...parsed, visitAt: now }));
  } else {
    // An untagged load (a reload, or the in-app browser restarting the visit)
    // refills any cookie the browser dropped from its mirror.
    readDurable(FIRST_COOKIE);
    readDurable(LAST_COOKIE);
  }

  // Ensure session id exists regardless.
  getOrCreateSessionId();
}

/** Visitor ID for anonymous→opt-in stitching: engage_vid (GTM, brand sites) when
 *  present, else the taker's own engage_session. Same precedence as the payload. */
export function getVisitorId(): string {
  return readCookie(VISITOR_ID_COOKIE) ?? getOrCreateSessionId();
}

/** Build the payload for inclusion in submission POSTs. Reads current cookies, mixes in
 *  page context + session id. Returns a shape the worker's persistAttribution expects. */
export function getAttributionPayload(): AttributionPayload {
  const consent = readCookie('engage_consent') === 'opted-out' ? 'opted-out' : 'default';
  if (consent === 'opted-out') {
    return {
      consent,
      submissionPageUri: location.href,
      submissionPageTitle: document.title,
      sessionId: getOrCreateSessionId(),
    };
  }

  let first: CapturedAttribution | undefined;
  const fc = readDurable(FIRST_COOKIE);
  if (fc) { try { first = JSON.parse(fc); } catch { /* */ } }

  let last: CapturedAttribution | undefined;
  const lc = readDurable(LAST_COOKIE);
  if (lc) { try { last = JSON.parse(lc); } catch { /* */ } }

  // Read the Meta-generated _fbp cookie for CAPI Event Match Quality.
  // This cookie is set by the Meta Pixel (browser-side GTM tag) and contains
  // a unique browser identifier Meta uses for identity matching.
  const fbpRaw = readCookie('_fbp');

  // Visitor ID for anonymous→opt-in stitching.
  // Prefer engage_vid (set by GTM on brand pages like the blog) — if the visitor
  // hit the blog before arriving here, this bridges the blog pageview to the opt-in.
  // Fall back to engage_session (the taker's own session ID).
  const sessionId = getOrCreateSessionId();
  const visitorId = readCookie(VISITOR_ID_COOKIE) ?? sessionId;

  return {
    first,
    last,
    submissionPageUri: location.href.slice(0, 255),
    submissionPageTitle: document.title.slice(0, 255),
    sessionId,
    consent,
    fbp: fbpRaw ?? undefined,
    visitorId,
    brandId: 'tsb',  // taker is TSB-brand; other brands reach EE via widget or HubSpot ingest
  };
}

/** Writes this browser's own visitor id into the page address as `ee_vid`,
 *  right before a Spiffy order form mounts. Spiffy records the embedding page's
 *  address (query string included) on the order as checkoutview.referrer, so
 *  the signed purchase webhook can tie this browser to the buyer
 *  (worker/src/visitor-ties.ts, how='checkout'). Always overwrites: an
 *  `ee_vid` arriving in a forwarded link names someone else's browser. Removed
 *  instead when the visitor has opted out of tracking. Never throws. */
export function stampVisitorOnAddress(): void {
  try {
    const u = new URL(window.location.href);
    if (readCookie('engage_consent') === 'opted-out') {
      u.searchParams.delete('ee_vid');
      u.searchParams.delete('ee_ref');
      u.searchParams.delete('ee_reft');
    } else {
      u.searchParams.set('ee_vid', getVisitorId());
      // The referral code this browser itself carried (last touch, else first),
      // so a membership bought here is credited even when the email card was
      // never shown. Always overwritten from the browser's own cookies: an
      // `ee_ref` in a forwarded link is someone else's click.
      const ref = ownReferralTouch();
      if (ref) { u.searchParams.set('ee_ref', ref.code); u.searchParams.set('ee_reft', ref.at); }
      else { u.searchParams.delete('ee_ref'); u.searchParams.delete('ee_reft'); }
    }
    if (u.toString() !== window.location.href) window.history.replaceState(window.history.state, '', u.toString());
  } catch {
    // never block the order form
  }
}

/** The referral code this browser arrived with and when, from its own
 *  attribution cookies. Last touch wins, as a registration's does. */
function ownReferralTouch(): { code: string; at: string } | null {
  for (const name of [LAST_COOKIE, FIRST_COOKIE]) {
    const raw = readDurable(name);
    if (!raw) continue;
    try {
      const c = JSON.parse(raw) as CapturedAttribution;
      const code = (c.referral_code ?? '').trim();
      if (code) return { code: code.slice(0, 64), at: c.visitAt ?? new Date().toISOString() };
    } catch { /* unreadable cookie: no touch */ }
  }
  return null;
}

/** This browser's visitor id, or null once the visitor has opted out of
 *  tracking — for the few calls that send it outside getAttributionPayload()
 *  (the portal sign-in check), which must honour the opt-out the same way. */
export function trackableVisitorId(): string | null {
  return readCookie('engage_consent') === 'opted-out' ? null : getVisitorId();
}
